import React, { useState, useEffect, useCallback } from 'react';
import { Megaphone, ChevronRight, CheckCheck } from 'lucide-react';
import {
  Announcement,
  getAnnouncements,
  getUnreadCount,
  markAllAnnouncementsAsRead,
} from '../../services/content/announcementService';
import { AnnouncementModal } from './AnnouncementModal';

// =====================================================
// KOMPONENTE
// =====================================================

export const AnnouncementsCard: React.FC = () => {
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [selectedAnnouncement, setSelectedAnnouncement] = useState<Announcement | null>(null);
  const [modalOpen, setModalOpen] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [announcementsRes, unreadRes] = await Promise.allSettled([
        getAnnouncements({ limit: 20 }),
        getUnreadCount(),
      ]);

      if (announcementsRes.status === 'fulfilled') {
        setAnnouncements(announcementsRes.value.announcements || []);
      }
      if (unreadRes.status === 'fulfilled') {
        setUnreadCount(unreadRes.value.count);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleOpenAnnouncement = (announcement: Announcement) => {
    setSelectedAnnouncement(announcement);
    setModalOpen(true);
  };

  const handleMarkedRead = useCallback(() => {
    setUnreadCount(prev => Math.max(0, prev - 1));
    // Announcement in der Liste als gelesen markieren
    setAnnouncements(prev =>
      prev.map(a =>
        a.id === selectedAnnouncement?.id ? { ...a, is_read: true } : a
      )
    );
  }, [selectedAnnouncement]);

  const handleMarkAllRead = useCallback(async (e: React.MouseEvent) => {
    e.stopPropagation();
    // Optimistic Update
    setAnnouncements(prev => prev.map(a => ({ ...a, is_read: true })));
    setUnreadCount(0);
    try {
      await markAllAnnouncementsAsRead();
    } catch {
      // Bei Fehler: neu laden, damit State konsistent bleibt
      loadData();
    }
  }, [loadData]);

  // Nichts anzeigen wenn keine Announcements vorhanden
  if (!loading && announcements.length === 0) return null;
  if (loading) return null;

  const formatDate = (dateStr: string): string => {
    return new Date(dateStr).toLocaleDateString('de-DE', {
      day: '2-digit',
      month: 'short',
    });
  };

  return (
    <>
      <div className="rounded-xl p-5 bg-white dark:bg-gray-900 border border-gray-200 dark:border-white/10 flex flex-col min-h-0 md:h-full">
        {/* Header */}
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-brand/10 text-brand">
            <Megaphone className="w-5 h-5" />
          </div>
          <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
            Neuigkeiten
          </span>
          {unreadCount > 0 && (
            <span className="min-w-[20px] h-5 px-1.5 text-[10px] font-semibold bg-brand text-gray-900 rounded-full flex items-center justify-center">
              {unreadCount}
            </span>
          )}
          {unreadCount > 0 && (
            <button
              type="button"
              onClick={handleMarkAllRead}
              className="ml-auto inline-flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400 hover:text-brand dark:hover:text-brand transition-colors"
              title="Alle als gelesen markieren"
            >
              <CheckCheck className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">Alle gelesen</span>
            </button>
          )}
        </div>

        {/* Announcement-Liste */}
        <div className="space-y-2 overflow-y-auto flex-1 min-h-0 max-h-[340px] md:max-h-[380px] xl:max-h-none scrollbar-styled touch-pan-y">
          {announcements.map(announcement => (
            <button
              key={announcement.id}
              onClick={() => handleOpenAnnouncement(announcement)}
              className="w-full text-left flex items-center gap-3 p-3 rounded-lg
                bg-gray-50 dark:bg-white/[0.03]
                border border-gray-100 dark:border-white/5
                hover:bg-gray-100 dark:hover:bg-white/[0.06]
                transition-colors cursor-pointer group"
            >
              {!announcement.is_read && (
                <div className="w-2 h-2 rounded-full bg-brand flex-shrink-0 self-start mt-1.5" />
              )}
              <div className="flex-1 min-w-0">
                <p className={`text-sm font-medium line-clamp-2 ${
                  announcement.is_read
                    ? 'text-gray-500 dark:text-gray-400'
                    : 'text-gray-900 dark:text-gray-100'
                }`}>
                  {announcement.title}
                </p>
                {announcement.excerpt && (
                  <p className="text-xs text-gray-500 dark:text-gray-400 truncate mt-0.5">
                    {announcement.excerpt}
                  </p>
                )}
              </div>
              <span className="text-xs text-gray-400 dark:text-gray-500 flex-shrink-0 self-start mt-0.5">
                {formatDate(announcement.published_at || announcement.created_at)}
              </span>
              <ChevronRight className="w-4 h-4 text-gray-300 dark:text-gray-600 flex-shrink-0 group-hover:text-brand transition-colors self-start mt-0.5" />
            </button>
          ))}
        </div>
      </div>

      {/* Modal */}
      <AnnouncementModal
        announcement={selectedAnnouncement}
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        onMarkedRead={handleMarkedRead}
      />
    </>
  );
};
